import React, { Activity, useCallback, useEffect, useRef, useState } from 'react';

import type { UUID } from '@aline/common';
import { ActivitySection, useActivityReplyHandler } from 'src/components';
import type { OnSubmitCommentReturn } from 'src/components';
import { ActivityFilterChips } from 'src/components/activity/ActivityFilterChips';
import type { ActivityFilter } from 'src/components/activity/email-thread';
import { ACTIVITY_FEED_FILTER, getEmailThread } from 'src/components/activity/email-thread';
import { EmailThreadView } from 'src/components/activity/EmailThreadView';
import type { ActivityNavigationRequest } from 'src/components/activity/use-activity-navigation';
import { useActivityNavigation } from 'src/components/activity/use-activity-navigation';
import { useOpenEmailThread } from 'src/components/activity/use-open-email-thread';
import { useStartAiThreadFromActivity } from 'src/components/activity/use-start-ai-thread';
import { useCurrentUserId } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { workflowTypename } from 'src/helpers';
import { useCreateComment, useWorkflowActivity } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

import { useThreadDeepLinkHandlers } from './use-thread-deep-link-handlers';

const useStyles = makeStyles()(theme => ({
  list: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: theme.spacing(2),
    minHeight: 0,
  },
}));

interface WorkflowActivitySectionProps {
  workflowId: UUID;
}

export function WorkflowActivitySection({ workflowId }: WorkflowActivitySectionProps): JSX.Element {
  const { classes } = useStyles();
  const currentUserId = useCurrentUserId();
  const navigation = useActivityNavigation(workflowId);
  const [filter, setFilter] = useState<ActivityFilter>('all');
  const { clearEmailThread, closeEmailThread, openEmailItemId, openEmailThread } =
    useOpenEmailThread();
  const anchoredEmailIdRef = useRef<UUID | null>(null);
  const activityForAnchorRef = useRef<{ id: string }[]>([]);
  const emailAnchor = getEmailActivityAnchor({
    anchoredEmailId: anchoredEmailIdRef.current,
    items: activityForAnchorRef.current,
    openEmailItemId,
  });
  anchoredEmailIdRef.current = emailAnchor;
  const {
    activity,
    fetchMoreActivity,
    fetchPreviousActivity,
    hasMore,
    hasPrevious,
    loadedAnchor,
    loading,
    loadingMore,
    loadingPrevious,
  } = useWorkflowActivity(workflowId, {
    anchor: navigation.request?.activityItemId ?? emailAnchor,
    fetchPolicy: 'cache-and-network',
    filter: filter === 'all' ? null : ACTIVITY_FEED_FILTER[filter],
    nextFetchPolicy: 'cache-first',
  });
  activityForAnchorRef.current = activity;
  const { createComment } = useCreateComment();
  const { onViewThread } = useThreadDeepLinkHandlers({ workflowId });
  const heldOpenEmailItem = useRef<(typeof activity)[number] | undefined>(undefined);
  const openEmailItemFromPage = openEmailItemId
    ? activity.find(item => item.id === openEmailItemId)
    : undefined;
  if (!openEmailItemId) heldOpenEmailItem.current = undefined;
  else if (openEmailItemFromPage) heldOpenEmailItem.current = openEmailItemFromPage;
  const openEmailItem =
    openEmailItemFromPage ??
    (heldOpenEmailItem.current?.id === openEmailItemId ? heldOpenEmailItem.current : undefined);
  const emailThread = openEmailItem ? getEmailThread(openEmailItem) : null;

  useEffect(() => {
    if (!openEmailItemId || loading) return;
    const item = activity.find(entry => entry.id === openEmailItemId);
    if (item) {
      if (!getEmailThread(item)) closeEmailThread();
      return;
    }
    if (loadedAnchor !== openEmailItemId) return;
    closeEmailThread();
  }, [activity, closeEmailThread, loadedAnchor, loading, openEmailItemId]);

  const handleCreateComment = useCallback(
    async (markdown: string): Promise<OnSubmitCommentReturn> => {
      if (!currentUserId) return null;
      const { data } = await createComment(workflowId, workflowTypename, {
        variables: {
          input: {
            commentProps: {
              authorId: currentUserId,
              commentableId: workflowId,
              commentableType: Apollo.CommentableType.Workflow,
              nodeRefId: null,
              parentCommentId: null,
              text: markdown,
            },
          },
        },
      });
      return data?.createComment.comment ?? null;
    },
    [createComment, currentUserId, workflowId]
  );

  const handleSubmitReply = useActivityReplyHandler({
    commentableId: workflowId,
    commentableType: Apollo.CommentableType.Workflow,
  });

  const handleSubmitAI = useStartAiThreadFromActivity({ workflowId });

  useShowAllOnNavigation({
    clearEmailThread,
    request: navigation.request,
    setFilter,
  });

  return (
    <>
      {/* The list stays mounted while a thread is open, so going back keeps its scroll and draft. */}
      <Activity mode={emailThread ? 'hidden' : 'visible'}>
        <div className={classes.list}>
          <ActivityFilterChips filter={filter} onChange={setFilter} />
          <ActivitySection
            showDayHeadings
            fetchMoreActivity={fetchMoreActivity}
            fetchPreviousActivity={fetchPreviousActivity}
            hasMore={hasMore}
            hasPrevious={hasPrevious}
            items={activity}
            loadedAnchor={loadedAnchor}
            loading={loading}
            loadingMore={loadingMore}
            loadingPrevious={loadingPrevious}
            navigation={navigation}
            placeholder="@ mention a user or AI in a comment…"
            showHeader={false}
            workflowId={workflowId}
            onOpenEmailThread={openEmailThread}
            onSubmitAI={handleSubmitAI}
            onSubmitComment={handleCreateComment}
            onSubmitReply={handleSubmitReply}
            onViewThread={onViewThread}
          />
        </div>
      </Activity>
      {emailThread && <EmailThreadView thread={emailThread} onSubmitReply={handleSubmitReply} />}
    </>
  );
}

/**
 * A deep link to a row must land on it, so a new link clears the filter and closes an open
 * thread. Each visit is handled once, so showing the panel again keeps the reader's filter.
 */
function useShowAllOnNavigation(input: {
  clearEmailThread: () => void;
  request: ActivityNavigationRequest | null;
  setFilter: (filter: ActivityFilter) => void;
}): void {
  const { clearEmailThread, request, setFilter } = input;
  const handledRequest = useRef<ActivityNavigationRequest | null>(null);

  useEffect(() => {
    if (!request || request === handledRequest.current) return;
    handledRequest.current = request;
    setFilter('all');
    clearEmailThread();
  }, [clearEmailThread, request, setFilter]);
}

/**
 * The activity window to load for an open email thread.
 *
 * A thread already on screen keeps the current window, so opening it does not
 * throw away the list's scroll. A thread that is not on screen, including one
 * opened from a reloaded URL, anchors the feed on that item and stays anchored
 * until the thread closes. Dropping the anchor once the item arrives would
 * refetch the first page and unload an older thread.
 */
function getEmailActivityAnchor(input: {
  anchoredEmailId: UUID | null;
  items: { id: string }[];
  openEmailItemId: UUID | null;
}): UUID | null {
  const { anchoredEmailId, items, openEmailItemId } = input;
  if (!openEmailItemId) return null;
  const onPage = items.some(item => item.id === openEmailItemId);
  if (onPage && anchoredEmailId !== openEmailItemId) return null;
  return openEmailItemId;
}
